// Building blocks shared by the one-device games: saved state, timers, team setup, scores.
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Link } from 'react-router';
import { MAX_TEAMS, MIN_TEAMS, TEAM_NAME_MAX, leaders, type Category, type Difficulty, type MinigameName, type MinigameStats, type Team } from '@shared/minigames';
import type { CardData } from '@shared/types';
import { Sheet } from '../../components/Sheet';
import { minigameApi } from './api';
import { isMuted, primeAudio, setMuted } from './sound';

/** useState that survives a refresh (localStorage). Bad or missing data falls back to `initial`. */
export function useStoredState<T>(key: string, initial: T | (() => T), valid: (v: unknown) => boolean = () => true) {
  const [value, setValue] = useState<T>(() => {
    try {
      const raw = localStorage.getItem(key);
      if (raw) {
        const parsed = JSON.parse(raw) as unknown;
        if (valid(parsed)) return parsed as T;
      }
    } catch {
      // Fall through to the default.
    }
    return typeof initial === 'function' ? (initial as () => T)() : initial;
  });
  useEffect(() => {
    try {
      if (value === null || value === undefined) localStorage.removeItem(key);
      else localStorage.setItem(key, JSON.stringify(value));
    } catch {
      // Storage full or blocked: the game still works, it just won't survive a refresh.
    }
  }, [key, value]);
  return [value, setValue] as const;
}

/** Epoch ms, re-rendering every `ms` while `active`. */
export function useNow(active: boolean, ms = 250): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!active) return;
    setNow(Date.now());
    const t = setInterval(() => setNow(Date.now()), ms);
    return () => clearInterval(t);
  }, [active, ms]);
  return now;
}

/** Keep the screen on while a round is running (where the browser supports it). */
export function useWakeLock(active: boolean): void {
  useEffect(() => {
    if (!active || !('wakeLock' in navigator)) return;
    let lock: { release: () => Promise<void> } | null = null;
    let cancelled = false;
    const request = () => {
      (navigator as unknown as { wakeLock: { request: (t: 'screen') => Promise<{ release: () => Promise<void> }> } }).wakeLock
        .request('screen')
        .then((l) => {
          if (cancelled) void l.release();
          else lock = l;
        })
        .catch(() => {});
    };
    request();
    const onVisible = () => document.visibilityState === 'visible' && request();
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', onVisible);
      void lock?.release().catch(() => {});
    };
  }, [active]);
}

export type StatsState = { status: 'loading' } | { status: 'error'; error: string } | { status: 'ready'; stats: MinigameStats };

/** How many cards the game has (counts only; the cards themselves are dealt during play). */
export function useStats(game: MinigameName): StatsState & { reload: () => void } {
  const [state, setState] = useState<StatsState>({ status: 'loading' });
  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    let alive = true;
    setState({ status: 'loading' });
    minigameApi
      .stats(game)
      .then((stats) => alive && setState({ status: 'ready', stats }))
      .catch((err: Error) => alive && setState({ status: 'error', error: err.message }));
    return () => {
      alive = false;
    };
  }, [game, attempt]);
  const reload = useCallback(() => setAttempt((n) => n + 1), []);
  return { ...state, reload };
}

export function StatsStatus({ stats }: { stats: StatsState & { reload: () => void } }) {
  if (stats.status === 'loading') return <p className="muted center mg-loading">Shuffling the deck...</p>;
  if (stats.status === 'error') {
    return (
      <section className="panel stack center">
        <h2>Couldn't reach the cards</h2>
        <p className="muted" style={{ margin: 0 }}>
          {stats.error} These games need an internet connection.
        </p>
        <button type="button" className="btn btn-primary" onClick={stats.reload}>
          Try again
        </button>
      </section>
    );
  }
  return null;
}

interface Dealt {
  sessionId: string | null;
  total: number;
  remaining: number;
  cards: Record<string, CardData>;
}

const NO_DEAL: Dealt = { sessionId: null, total: 0, remaining: 0, cards: {} };

/**
 * Cards come from the server a few at a time. This keeps the ones dealt so far (so a refresh can
 * redraw the screen) and fetches more as the deck runs low.
 */
export function useDealer(game: MinigameName) {
  const [dealt, setDealt] = useStoredState<Dealt>(`cm:mg:${game}:dealt`, NO_DEAL, (v) => typeof (v as Dealt)?.cards === 'object');
  const fetching = useRef(false);

  const start = useCallback(
    async (filters: { difficulties?: Difficulty[]; categories?: Category[] }) => {
      const batch = await minigameApi.deal(game, filters);
      const next: Dealt = { sessionId: batch.sessionId ?? null, total: batch.total ?? batch.cards.length, remaining: batch.remaining, cards: {} };
      for (const c of batch.cards) next.cards[c.id] = c;
      setDealt(next);
      return { ids: batch.cards.map((c) => c.id), total: next.total };
    },
    [game, setDealt],
  );

  /** Fetch the next batch. Resolves to the new ids ([] when the deck is used up or expired). */
  const more = useCallback(async (): Promise<string[]> => {
    if (fetching.current || !dealt.sessionId || dealt.remaining <= 0) return [];
    fetching.current = true;
    try {
      const batch = await minigameApi.more(dealt.sessionId);
      setDealt((d) => {
        const cards = { ...d.cards };
        for (const c of batch.cards) cards[c.id] = c;
        return { ...d, remaining: batch.remaining, cards };
      });
      return batch.cards.map((c) => c.id);
    } catch {
      // Expired or offline: play on with the cards already dealt.
      setDealt((d) => ({ ...d, remaining: 0 }));
      return [];
    } finally {
      fetching.current = false;
    }
  }, [dealt.sessionId, dealt.remaining, setDealt]);

  const byId = useMemo(() => new Map(Object.entries(dealt.cards)), [dealt.cards]);
  const reset = useCallback(() => setDealt(NO_DEAL), [setDealt]);
  return { start, more, byId, total: dealt.total, hasMore: dealt.remaining > 0, reset };
}

/** Top up a running game's deck when it gets low. */
export function useTopUp<S extends { deck: { draw: string[] } }>(
  state: S | null,
  dealer: ReturnType<typeof useDealer>,
  apply: (ids: string[]) => void,
  threshold = 4,
) {
  const low = Boolean(state && state.deck.draw.length < threshold && dealer.hasMore);
  useEffect(() => {
    if (!low) return;
    void dealer.more().then((ids) => ids.length && apply(ids));
  }, [low, dealer, apply]);
}

/** Page frame for a mini-game: back link, title, sound toggle and a menu. */
export function GameFrame({ title, children, menu }: { title: string; children: ReactNode; menu?: ReactNode }) {
  const [open, setOpen] = useState(false);
  const [muted, setMutedState] = useState(isMuted);
  return (
    <div className="game mg">
      <header className="game-header">
        <div className="inner">
          <div className="row between mg-bar" style={{ flexWrap: 'nowrap' }}>
            <Link to="/games" className="btn btn-ghost btn-sm" aria-label="Back to all games">
              ← Games
            </Link>
            <strong className="mg-title">{title}</strong>
            <button
              type="button"
              className="icon-btn"
              aria-label={muted ? 'Turn sound on' : 'Turn sound off'}
              aria-pressed={!muted}
              onClick={() => {
                setMuted(!muted);
                setMutedState(!muted);
                if (muted) primeAudio();
              }}
            >
              {muted ? '🔇' : '🔊'}
            </button>
            {menu && (
              <button type="button" className="icon-btn" aria-label="Game menu" onClick={() => setOpen(true)}>
                ☰
              </button>
            )}
          </div>
        </div>
      </header>
      {children}
      {open && menu && (
        <Sheet title={title} onClose={() => setOpen(false)}>
          <div className="menu-list" onClick={() => setOpen(false)}>
            {menu}
          </div>
        </Sheet>
      )}
    </div>
  );
}

export function TeamEditor({ names, onChange, placeholders }: { names: string[]; onChange: (names: string[]) => void; placeholders?: string[] }) {
  const hints = placeholders ?? ['Kids', 'Parents', 'Mamas', 'Chachas'];
  return (
    <div className="stack" style={{ gap: 8 }}>
      {names.map((name, i) => (
        <div key={i} className="row" style={{ flexWrap: 'nowrap' }}>
          <span className={`mg-team-dot t${i}`} aria-hidden="true" />
          <input
            className="input"
            value={name}
            maxLength={TEAM_NAME_MAX}
            placeholder={hints[i] ?? `Team ${i + 1}`}
            aria-label={`Team ${i + 1} name`}
            onChange={(e) => onChange(names.map((n, j) => (j === i ? e.target.value : n)))}
          />
          {names.length > MIN_TEAMS && (
            <button type="button" className="icon-btn" aria-label={`Remove team ${i + 1}`} onClick={() => onChange(names.filter((_, j) => j !== i))}>
              ✕
            </button>
          )}
        </div>
      ))}
      {names.length < MAX_TEAMS && (
        <button type="button" className="btn btn-outline btn-sm" style={{ alignSelf: 'flex-start' }} onClick={() => onChange([...names, ''])}>
          + Add a team
        </button>
      )}
    </div>
  );
}

/** Team names as typed, with the placeholder used for blanks. */
export function teamNamesOrHints(names: string[], placeholders = ['Kids', 'Parents', 'Mamas', 'Chachas']): string[] {
  return names.map((n, i) => n.trim() || placeholders[i] || `Team ${i + 1}`);
}

export function Choice<T extends string | number>({ value, options, onChange, label }: { value: T; options: [T, string][]; onChange: (v: T) => void; label: string }) {
  return (
    <div className="stack" style={{ gap: 6 }}>
      <strong className="small">{label}</strong>
      <div className="segmented" role="group" aria-label={label}>
        {options.map(([v, text]) => (
          <button key={String(v)} type="button" aria-pressed={v === value} onClick={() => onChange(v)}>
            {text}
          </button>
        ))}
      </div>
    </div>
  );
}

/** Multi-select chips; at least one stays on. */
export function MultiChoice<T extends string>({ values, options, onChange, label }: { values: T[]; options: [T, string][]; onChange: (v: T[]) => void; label: string }) {
  return (
    <div className="stack" style={{ gap: 6 }}>
      <strong className="small">{label}</strong>
      <div className="segmented" role="group" aria-label={label}>
        {options.map(([v, text]) => {
          const on = values.includes(v);
          return (
            <button
              key={v}
              type="button"
              aria-pressed={on}
              onClick={() => {
                if (on && values.length === 1) return;
                onChange(on ? values.filter((x) => x !== v) : [...values, v]);
              }}
            >
              {text}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export function TeamScores({ teams, active }: { teams: Team[]; active?: number | null }) {
  return (
    <div className="mg-scores" aria-label="Scores">
      {teams.map((t, i) => (
        <div key={i} className={`mg-score t${i} ${active === i ? 'active' : ''}`}>
          <span className="name">{t.name}</span>
          <span className="pts">{t.score}</span>
        </div>
      ))}
    </div>
  );
}

/** Big countdown with a draining bar. */
export function Countdown({ endsAt, now, total }: { endsAt: number; now: number; total: number }) {
  const left = Math.max(0, Math.ceil((endsAt - now) / 1000));
  const pct = Math.max(0, Math.min(100, ((endsAt - now) / (total * 1000)) * 100));
  return (
    <div className={`mg-countdown ${left <= 10 ? 'low' : ''}`} role="timer" aria-label={`${left} seconds left`}>
      <span className="secs">{left}</span>
      <span className="bar" aria-hidden="true">
        <span style={{ width: `${pct}%` }} />
      </span>
    </div>
  );
}

/** Calls `onTick(secondsLeft)` once per second in the last `from` seconds and `onEnd` when time is up. */
export function useTimerEffects(endsAt: number | null, now: number, onEnd: () => void, onTick?: (left: number) => void, from = 5) {
  const lastTick = useRef<number | null>(null);
  const ended = useRef<number | null>(null);
  useEffect(() => {
    if (!endsAt) return;
    const left = Math.ceil((endsAt - now) / 1000);
    if (left <= 0) {
      if (ended.current !== endsAt) {
        ended.current = endsAt;
        onEnd();
      }
      return;
    }
    if (left <= from && lastTick.current !== left) {
      lastTick.current = left;
      onTick?.(left);
    }
  }, [endsAt, now, onEnd, onTick, from]);
}

export function GameOverPanel({ teams, onAgain, onSetup, children }: { teams: Team[]; onAgain: () => void; onSetup: () => void; children?: ReactNode }) {
  const top = leaders(teams);
  const ranked = [...teams].sort((a, b) => b.score - a.score);
  return (
    <main className="game-main mg-main">
      <div className="winner-banner pop-in">
        <div>🏆 {top.length === 1 ? 'Winners' : top.length > 1 ? "It's a tie" : 'Game over'}</div>
        <div className="who">{top.length ? top.map((t) => t.name).join(' & ') : 'Nobody scored. Rematch?'}</div>
      </div>
      <ol className="mg-final">
        {ranked.map((t, i) => (
          <li key={i}>
            <span>{t.name}</span>
            <strong>{t.score}</strong>
          </li>
        ))}
      </ol>
      {children}
      <div className="stack">
        <button type="button" className="btn btn-primary btn-lg btn-block" onClick={onAgain}>
          Rematch, same teams
        </button>
        <button type="button" className="btn btn-outline btn-block" onClick={onSetup}>
          Change teams or settings
        </button>
      </div>
    </main>
  );
}

/** "Hold to peek": content shows only while a finger (or the space bar) is down on the button. */
export function HoldToPeek({ children, label = 'Hold to peek' }: { children: ReactNode; label?: string }) {
  const [held, setHeld] = useState(false);
  const up = () => setHeld(false);
  return (
    <div
      className={`mg-peek ${held ? 'held' : ''}`}
      role="button"
      tabIndex={0}
      aria-pressed={held}
      aria-label={held ? 'Showing the card. Let go to hide it.' : `${label}. Only the actor should look.`}
      onPointerDown={(e) => {
        e.currentTarget.setPointerCapture?.(e.pointerId);
        setHeld(true);
      }}
      onPointerUp={up}
      onPointerCancel={up}
      onLostPointerCapture={up}
      onContextMenu={(e) => e.preventDefault()}
      onKeyDown={(e) => {
        if (e.key === ' ' || e.key === 'Enter') {
          e.preventDefault();
          setHeld(true);
        }
      }}
      onKeyUp={up}
      onBlur={up}
    >
      {held ? (
        <div className="mg-peek-body">{children}</div>
      ) : (
        <div className="mg-peek-cover">
          <span className="eye" aria-hidden="true">
            🙈
          </span>
          <strong>{label}</strong>
          <span className="small">Only the actor looks</span>
        </div>
      )}
    </div>
  );
}
